import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Badge, Button, Empty, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';

const ICONS = { home: '🏠', work: '🏢', other: '📍' };

const AddressesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.addresses.list()
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    const ok = await toast.confirm({
      title: 'Delete address',
      message: 'Remove this saved address?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await api.addresses.remove(a._id);
    load();
  };

  const setDefault = async (a) => {
    await api.addresses.setDefault(a._id);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Saved addresses"
        subtitle={`${items.length} saved`}
        right={<Button title="+ Add new" size="sm" onClick={() => navigate('/addresses/new')} />}
      />

      {loading ? <Loading /> : !items.length ? (
        <Empty
          emoji="📍"
          title="No saved addresses"
          text="Add an address so bookings and deliveries reach you faster."
          actionLabel="Add address"
          onAction={() => navigate('/addresses/new')}
        />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 55)} className="card sh-sm">
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <span
                  style={{
                    width: 40, height: 40, borderRadius: 12, background: colors.primaryLight,
                    display: 'grid', placeItems: 'center', fontSize: 18, flex: '0 0 auto',
                  }}
                >
                  {ICONS[item.label] || '📍'}
                </span>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <span className="t-title">{(item.label || '').toUpperCase()}</span>
                    {item.isDefault ? <Badge value="Default" color={colors.success} /> : null}
                  </div>
                  <div className="t-small" style={{ marginTop: 3, lineHeight: 1.55 }}>
                    {[item.line1, item.line2, item.landmark, item.city, item.pincode]
                      .filter(Boolean).join(', ')}
                  </div>
                  {item.phone ? <div className="t-tiny" style={{ marginTop: 3 }}>📞 {item.phone}</div> : null}
                </div>
              </div>

              <div
                className="row wrap"
                style={{ gap: 16, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.line}` }}
              >
                {!item.isDefault ? (
                  <button type="button" className="link-btn" onClick={() => setDefault(item)}>
                    Set as default
                  </button>
                ) : null}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate(`/addresses/${item._id}/edit`, { state: { address: item } })}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="link-btn"
                  style={{ color: colors.danger }}
                  onClick={() => remove(item)}
                >
                  Delete
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressesPage;
